import SquareIcon from "@mui/icons-material/Square";
import React, {useEffect, useRef, useState} from "react";
import {HourglassEmpty} from "@mui/icons-material";
import Box from "@mui/material/Box";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import StopIcon from "@mui/icons-material/Stop";
import ReplayIcon from "@mui/icons-material/Replay";

export const RecordingStateDisplay = React.memo(({
                                                     recordingInProgress,
                                                     localRecordingInProgress,
                                                 }) => {
    const [recordTime, setRecordTime] = useState(0);
    const timerRef = useRef(null);
    const startedAtRef = useRef(null);

    useEffect(() => {
        if (recordingInProgress) {
            const startedAt = Date.now();
            startedAtRef.current = startedAt;

            const tick = () => {
                setRecordTime(Math.floor((Date.now() - startedAt) / 1000));
            };

            tick();
            timerRef.current = setInterval(tick, 1000);
        } else {
            clearInterval(timerRef.current);
            timerRef.current = null;
            startedAtRef.current = null;
            setRecordTime(0);
        }

        return () => {
            clearInterval(timerRef.current);
            timerRef.current = null;
        };
    }, [recordingInProgress]);

    const isActive = recordingInProgress || localRecordingInProgress;

    const formatTime = ({recordTime}) => {
        const hours = Math.floor(recordTime / 3600).toString().padStart(2, '0');
        const minutes = Math.floor((recordTime % 3600) / 60).toString().padStart(2, '0');
        const seconds = (recordTime % 60).toString().padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const getStatusText = () => {
        if (recordingInProgress) {
            return formatTime({recordTime});
        }
        if (localRecordingInProgress) {
            return "Starting...";
        }
        return "Ready";
    };

    return (
        <>
            <style>
                {`
                    @keyframes flashing {
                        0% { opacity: 1; }
                        50% { opacity: 0.3; }
                        100% { opacity: 1; }
                    }
                    @keyframes pulse-ring {
                        0% { transform: scale(0.9); opacity: 0.8; }
                        50% { transform: scale(1.1); opacity: 0.4; }
                        100% { transform: scale(0.9); opacity: 0.8; }
                    }
                `}
            </style>
            <Box sx={{ position: 'relative' }}>
                {/* Main pill display */}
                <Box sx={{
                    background: isActive 
                        ? 'linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 100%)'
                        : '#F3F4F6',
                    color: isActive ? 'white' : '#6B7280',
                    minWidth: '130px',
                    height: '40px',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    px: 2,
                    boxShadow: isActive 
                        ? '0 4px 12px rgba(0, 0, 0, 0.15)'
                        : '0 1px 3px rgba(0, 0, 0, 0.08)',
                    border: isActive ? 'none' : '1px solid #E5E7EB',
                    transition: 'all 0.3s ease',
                }}>
                    {/* Recording indicator dot */}
                    <Box sx={{
                        position: 'relative',
                        width: '10px',
                        height: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        {recordingInProgress && (
                            <Box sx={{
                                position: 'absolute',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(239, 68, 68, 0.3)',
                                animation: 'pulse-ring 1.5s ease-in-out infinite',
                            }} />
                        )}
                        <Box sx={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: isActive ? '#EF4444' : '#9CA3AF',
                            animation: recordingInProgress ? 'flashing 1s infinite' : 'none',
                            position: 'relative',
                            zIndex: 1,
                        }} />
                    </Box>
                    
                    {/* Status text */}
                    <Box sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '13px',
                        fontWeight: 600,
                        letterSpacing: recordingInProgress ? '0.5px' : '0.2px',
                        fontVariantNumeric: 'tabular-nums',
                        userSelect: 'none',
                    }}>
                        {getStatusText()}
                    </Box>
                </Box>
            </Box>
        </>
    );
});

const NewRecordButton = React.memo(({
                                        recordingInProgress,
                                        localRecordingInProgress,
                                        startRecording,
                                        stopRecording,
                                        recordingPerformed,
                                        onStartRecording,
    disabled,
                                    }) => {
    const [recordTime, setRecordTime] = useState(0);
    const timerRef = useRef(null);
    const startedAtRef = useRef(null);

    useEffect(() => {
        if (recordingInProgress) {
            const startedAt = Date.now();
            startedAtRef.current = startedAt;

            const tick = () => {
                setRecordTime(Math.floor((Date.now() - startedAt) / 1000));
            };

            tick();
            timerRef.current = setInterval(tick, 1000);
        } else {
            clearInterval(timerRef.current);
            timerRef.current = null;
            startedAtRef.current = null;
            setRecordTime(0);
        }

        return () => {
            clearInterval(timerRef.current);
            timerRef.current = null;
        };
    }, [recordingInProgress]);

    const buttonStyle = (recordingPerformed) => {
        return ({
            backgroundColor: recordingInProgress || localRecordingInProgress ? "#000" : recordingPerformed ? "#E3E5E8" : disabled ? "#E0B0A0" : "#F23000",
            color: recordingPerformed ? "#1a1a1a" : "white",
            width: "120px",
            height: "40px",
            borderRadius: "6px",
            // Padding 24Px on left and right, and 14px on top and bottom
            // padding: "24px 14px",
            display: 'flex', // Ensure flexbox is used
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            gap: "8px",
            cursor: disabled ? 'not-allowed' : 'pointer',
        })
    }

    const ButtonIcon = () => {
        const iconStyle = recordingInProgress || localRecordingInProgress
            ? {
                color: '#F23000',
                animation: 'flashing 1s infinite'
            }
            : {
                color: '#F23000'
            };


        if (recordingInProgress) {
            return (
                <StopIcon style={iconStyle}/>
            );
        }

        if (localRecordingInProgress) {
            return (
                <HourglassEmpty/>
            );
        }

        return (
            <FiberManualRecordIcon style={{color: '#FFFFFF'}}/>
        );
    }

    const handleButtonClick = async () => {

        if (localRecordingInProgress && !recordingInProgress) return; // Prevents button action if disabled

        if (recordingPerformed) {
            // Refresh the page
            window.location.reload();
            return;
        }

        if (recordingInProgress) {
            await stopRecording();
        } else if (!disabled) { // Disabled is only to stop the start of a recording.
            onStartRecording?.();
            await startRecording();
        }
    }

    const formatTime = ({recordTime}) => {
        const hours = Math.floor(recordTime / 3600).toString().padStart(2, '0');
        const minutes = Math.floor((recordTime % 3600) / 60).toString().padStart(2, '0');
        const seconds = (recordTime % 60).toString().padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const ButtonText = () => {
        if (recordingInProgress) {
            return (
                <Box style={textStyle}>
                    {formatTime({recordTime})}
                </Box>
            );
        }

        if (localRecordingInProgress) {
            return (
                <Box style={textStyle}>
                    Starting
                </Box>
            );
        }

        if (recordingPerformed) {
            return (
                <Box style={textStyle}>
                    New Recording
                </Box>
            );
        }

        return (
            <Box style={textStyle}>
                Record
            </Box>
        );
    }

    const textStyle = {
        fontFamily: "Inter",
        fontStyle: "normal",
        fontSize: "12px",
        fontWeight: 500,
        lineHeight: "16px",
        letterSpacing: "0.14px",
        color: recordingPerformed ? "#1a1a1a" : "white",
        // disable highlight on double click
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        KhtmlUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
    }

    const textContainerStyle = {
        width: "60px",
        cursor: disabled ? 'not-allowed' : 'pointer',
    }

    return (
        <>
            <style>
                {`
                    @keyframes flashing {
                        0% { opacity: 1; }
                        50% { opacity: 0; }
                        100% { opacity: 1; }
                    }
                `}
            </style>
            {<Box onClick={handleButtonClick} sx={buttonStyle(recordingPerformed)}>
                {!recordingPerformed && <ButtonIcon/>}
                <Box style={textContainerStyle}>{ButtonText()}</Box>
            </Box>}
        </>);
});

export default NewRecordButton;
